import React, { useState, useEffect } from 'react';
import { UserPlus, ChevronDown, ChevronUp, Plus, Upload, Trash2, FileText, X, Search, Phone, Mail } from 'lucide-react';
import { customersAPI } from '../../services/api';
import './AdminSections.css';

const CRM_STAGES = [
  'Επικοινωνία',
  'Ραντεβού',
  'Προσφορά',
  'Τροποποίηση Προσφοράς',
  'Ολοκλήρωση Έργου',
  'Απόρριψη Έργου'
];

const FINAL_STAGES = ['Ολοκλήρωση Έργου', 'Απόρριψη Έργου'];

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', phone: '', email: '', notes: '', password: '' });
  const [crmForm, setCrmForm] = useState({ stage: 'Επικοινωνία', notes: '' });
  const [creating, setCreating] = useState(false);
  const [addingCRM, setAddingCRM] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('crm');
  const [createdCredentials, setCreatedCredentials] = useState(null);

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    try {
      const res = await customersAPI.getAll();
      setCustomers(res.data);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await customersAPI.create(createForm);
      setCreatedCredentials({
        username: res.data.username,
        password: res.data.password,
        customer_number: res.data.customer_number,
      });
      setShowCreateModal(false);
      setCreateForm({ first_name: '', last_name: '', phone: '', email: '', notes: '', password: '' });
      await loadCustomers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα');
    } finally {
      setCreating(false);
    }
  };

  const handleAddCRM = async (customerId) => {
    setAddingCRM(true);
    try {
      await customersAPI.addCRM(customerId, crmForm);
      setCrmForm({ stage: 'Επικοινωνία', notes: '' });
      await loadCustomers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Σφάλμα');
    } finally {
      setAddingCRM(false);
    }
  };

  const handleFileUpload = async (customerId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      await customersAPI.uploadFile(customerId, file);
      await loadCustomers();
    } catch (error) {
      alert('Σφάλμα ανεβάσματος');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (customerId, fileId) => {
    if (!window.confirm('Διαγραφή αρχείου;')) return;
    try {
      await customersAPI.deleteFile(customerId, fileId);
      await loadCustomers();
    } catch (error) {
      alert('Σφάλμα');
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Διαγραφή πελάτη;')) return;
    try {
      await customersAPI.delete(customerId);
      setExpandedCustomer(null);
      await loadCustomers();
    } catch (error) {
      alert('Σφάλμα');
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      'Επικοινωνία': '#2196F3',
      'Ραντεβού': '#FF9800',
      'Προσφορά': '#9C27B0',
      'Τροποποίηση Προσφοράς': '#E91E63',
      'Ολοκλήρωση Έργου': '#4CAF50',
      'Απόρριψη Έργου': '#f44336'
    };
    return colors[stage] || '#666';
  };

  const filtered = searchQuery
    ? customers.filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.phone || '').includes(searchQuery) ||
        (c.email || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : customers;

  return (
    <div className="admin-section" data-testid="customer-management">
      <div className="section-header">
        <h2><UserPlus size={24} /> Διαχείριση Πελατών</h2>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)} data-testid="create-customer-btn">
          <Plus size={18} /> Νέος Πελάτης
        </button>
      </div>

      <div style={{ marginBottom: '1.5rem', maxWidth: 400, position: 'relative' }}>
        <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
        <input
          type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Αναζήτηση πελάτη..."
          style={{ width: '100%', padding: '0.875rem 0.875rem 0.875rem 2.5rem', border: '1px solid #d0d0d0', fontSize: '1rem', fontFamily: 'inherit' }}
          data-testid="search-customer"
        />
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', padding: '2rem' }}>Φόρτωση...</p>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <UserPlus size={48} />
          <p>{searchQuery ? 'Δεν βρέθηκαν πελάτες' : 'Δεν υπάρχουν πελάτες'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(customer => (
            <div key={customer.id} style={{ border: '1px solid #e0e0e0', background: '#fff' }} data-testid={`customer-${customer.id}`}>
              <div
                onClick={() => { setExpandedCustomer(expandedCustomer === customer.id ? null : customer.id); setActiveSubTab('crm'); }}
                style={{
                  padding: '1rem 1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', background: expandedCustomer === customer.id ? '#f5f5ff' : 'transparent'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#667eea', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.9rem' }}>
                    {customer.first_name[0]}{customer.last_name[0]}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 500, color: '#1a1a1a' }}>{customer.first_name} {customer.last_name}</span>
                      {customer.customer_number && (
                        <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '0.1rem 0.4rem', fontSize: '0.7rem', borderRadius: 3, fontFamily: 'monospace' }}>
                          {customer.customer_number}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#999', display: 'flex', gap: '1rem', marginTop: 2 }}>
                      {customer.phone && <span><Phone size={12} style={{ verticalAlign: 'middle' }} /> {customer.phone}</span>}
                      {customer.email && <span><Mail size={12} style={{ verticalAlign: 'middle' }} /> {customer.email}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {customer.crm_entries?.length > 0 && (
                    <span style={{
                      padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 500,
                      background: `${getStageColor(customer.crm_entries[customer.crm_entries.length - 1].stage)}15`,
                      color: getStageColor(customer.crm_entries[customer.crm_entries.length - 1].stage),
                      borderRadius: 3
                    }}>
                      {customer.crm_entries[customer.crm_entries.length - 1].stage}
                    </span>
                  )}
                  {customer.is_finalized && (
                    <span style={{ fontSize: '0.7rem', color: '#999', fontStyle: 'italic' }}>Κλειστό</span>
                  )}
                  {expandedCustomer === customer.id ? <ChevronUp size={20} color="#666" /> : <ChevronDown size={20} color="#666" />}
                </div>
              </div>

              {expandedCustomer === customer.id && (
                <div style={{ borderTop: '1px solid #e0e0e0' }}>
                  <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0', background: '#fafafa', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex' }}>
                      {[{ id: 'crm', label: 'CRM' }, { id: 'info', label: 'Στοιχεία' }, { id: 'files', label: 'Αρχεία' }].map(tab => (
                        <button key={tab.id} onClick={() => setActiveSubTab(tab.id)}
                          style={{
                            padding: '0.75rem 1.5rem', border: 'none',
                            borderBottom: activeSubTab === tab.id ? '2px solid #667eea' : '2px solid transparent',
                            background: 'transparent', color: activeSubTab === tab.id ? '#667eea' : '#666',
                            fontWeight: activeSubTab === tab.id ? 600 : 400, cursor: 'pointer', fontSize: '0.9rem'
                          }}
                        >{tab.label}</button>
                      ))}
                    </div>
                    <button onClick={() => handleDelete(customer.id)}
                      style={{ padding: '0.5rem 1rem', margin: '0.5rem', border: '1px solid rgba(244,67,54,0.3)', background: 'rgba(244,67,54,0.05)', color: '#d32f2f', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 4 }}
                    ><Trash2 size={14} /> Διαγραφή</button>
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    {/* CRM Tab */}
                    {activeSubTab === 'crm' && (
                      <div>
                        {!customer.is_finalized && (
                          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div>
                              <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: 4 }}>Στάδιο</label>
                              <select value={crmForm.stage} onChange={(e) => setCrmForm({ ...crmForm, stage: e.target.value })}
                                style={{ padding: '0.6rem', border: '1px solid #d0d0d0', fontSize: '0.9rem', fontFamily: 'inherit', minWidth: 200 }}
                                data-testid="crm-stage-select"
                              >
                                {CRM_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>
                            <div style={{ flex: 1, minWidth: 200 }}>
                              <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: 4 }}>Σημειώσεις</label>
                              <input type="text" value={crmForm.notes} onChange={(e) => setCrmForm({ ...crmForm, notes: e.target.value })}
                                placeholder="Σημειώσεις..." style={{ width: '100%', padding: '0.6rem', border: '1px solid #d0d0d0', fontSize: '0.9rem', fontFamily: 'inherit' }}
                                data-testid="crm-notes-input"
                              />
                            </div>
                            <button onClick={() => handleAddCRM(customer.id)} className="btn-primary" disabled={addingCRM}
                              data-testid="crm-add-btn"
                            >
                              <Plus size={16} /> {addingCRM ? '...' : 'Προσθήκη'}
                            </button>
                          </div>
                        )}

                        {(customer.crm_entries || []).length === 0 ? (
                          <p style={{ color: '#999', textAlign: 'center', padding: '2rem' }}>Δεν υπάρχουν εγγραφές CRM</p>
                        ) : (
                          <div style={{ position: 'relative', paddingLeft: '2rem' }}>
                            <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: '#e0e0e0' }} />
                            {[...customer.crm_entries].reverse().map((entry, idx) => (
                              <div key={entry.id || idx} style={{ position: 'relative', marginBottom: '1.25rem', paddingBottom: '0.5rem' }}>
                                <div style={{
                                  position: 'absolute', left: '-1.55rem', top: 4, width: 12, height: 12,
                                  borderRadius: '50%', background: getStageColor(entry.stage), border: '2px solid #fff', boxShadow: '0 0 0 2px ' + getStageColor(entry.stage)
                                }} />
                                <div style={{ padding: '0.75rem 1rem', background: '#fafafa', border: '1px solid #e0e0e0', borderLeft: `3px solid ${getStageColor(entry.stage)}` }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                    <span style={{ fontWeight: 600, color: getStageColor(entry.stage), fontSize: '0.9rem' }}>{entry.stage}</span>
                                    <span style={{ fontSize: '0.75rem', color: '#999' }}>
                                      {new Date(entry.date).toLocaleString('el-GR')}
                                    </span>
                                  </div>
                                  {entry.notes && <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>{entry.notes}</p>}
                                  <span style={{ fontSize: '0.7rem', color: '#bbb' }}>απο: {entry.created_by}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Info Tab */}
                    {activeSubTab === 'info' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxWidth: 600 }}>
                        <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Όνομα</span><p style={{ margin: '0.2rem 0 0', fontWeight: 500 }}>{customer.first_name}</p></div>
                        <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Επώνυμο</span><p style={{ margin: '0.2rem 0 0', fontWeight: 500 }}>{customer.last_name}</p></div>
                        <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Τηλέφωνο</span><p style={{ margin: '0.2rem 0 0' }}>{customer.phone || '-'}</p></div>
                        <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Email</span><p style={{ margin: '0.2rem 0 0' }}>{customer.email || '-'}</p></div>
                        {customer.customer_number && (
                          <div style={{ gridColumn: '1 / -1', background: '#f5f5ff', padding: '0.75rem 1rem', border: '1px solid #d0d0ff', marginTop: '0.5rem' }}>
                            <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#667eea', marginBottom: '0.5rem' }}>Στοιχεία Σύνδεσης Πύλης Πελάτη</p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                              <div>
                                <span style={{ fontSize: '0.75rem', color: '#999' }}>Κωδικός</span>
                                <p style={{ margin: '0.1rem 0 0', fontFamily: 'monospace', fontWeight: 600 }}>{customer.customer_number}</p>
                              </div>
                              <div>
                                <span style={{ fontSize: '0.75rem', color: '#999' }}>Username</span>
                                <p style={{ margin: '0.1rem 0 0', fontFamily: 'monospace', fontWeight: 600 }}>{customer.username}</p>
                              </div>
                              <div>
                                <span style={{ fontSize: '0.75rem', color: '#999' }}>Password</span>
                                <p style={{ margin: '0.1rem 0 0', fontFamily: 'monospace', fontWeight: 600 }}>{customer.password}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        {customer.notes && (
                          <div style={{ gridColumn: '1 / -1' }}><span style={{ fontSize: '0.8rem', color: '#999' }}>Σημειώσεις</span><p style={{ margin: '0.2rem 0 0', lineHeight: 1.5 }}>{customer.notes}</p></div>
                        )}
                        <div style={{ gridColumn: '1 / -1', fontSize: '0.8rem', color: '#bbb', marginTop: '0.5rem' }}>
                          Δημιουργήθηκε: {new Date(customer.created_at).toLocaleDateString('el-GR')}
                        </div>
                      </div>
                    )}

                    {/* Files Tab */}
                    {activeSubTab === 'files' && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                          <h3 style={{ fontSize: '1rem', fontWeight: 500, margin: 0 }}>Αρχεία Πελάτη</h3>
                          <label className="btn-primary" style={{ cursor: 'pointer', opacity: uploadingFile ? 0.5 : 1 }}>
                            <Upload size={16} /> {uploadingFile ? '...' : 'Ανέβασμα'}
                            <input type="file" onChange={(e) => handleFileUpload(customer.id, e)} style={{ display: 'none' }} disabled={uploadingFile} />
                          </label>
                        </div>
                        {(customer.files || []).length === 0 ? (
                          <p style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Δεν υπάρχουν αρχεία</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {customer.files.map(f => (
                              <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', border: '1px solid #e0e0e0', background: '#fafafa' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                  <FileText size={18} color="#667eea" />
                                  <div>
                                    <p style={{ margin: 0, fontWeight: 500, fontSize: '0.9rem' }}>{f.filename}</p>
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#999' }}>{(f.file_size / 1024).toFixed(1)} KB - {f.uploaded_by}</p>
                                  </div>
                                </div>
                                <button onClick={() => handleDeleteFile(customer.id, f.id)} className="btn-icon danger"><Trash2 size={16} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay-admin" onClick={() => setShowCreateModal(false)}>
          <div className="modal-admin" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-admin">
              <h3>Νέος Πελάτης</h3>
              <button onClick={() => setShowCreateModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} className="modal-form">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group-admin">
                  <label>Όνομα *</label>
                  <input type="text" value={createForm.first_name} onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })} required data-testid="customer-first-name" />
                </div>
                <div className="form-group-admin">
                  <label>Επώνυμο *</label>
                  <input type="text" value={createForm.last_name} onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })} required data-testid="customer-last-name" />
                </div>
              </div>
              <div className="form-group-admin">
                <label>Τηλέφωνο</label>
                <input type="text" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} data-testid="customer-phone" />
              </div>
              <div className="form-group-admin">
                <label>Email</label>
                <input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="customer-email" />
              </div>
              <div className="form-group-admin">
                <label>Σημειώσεις</label>
                <textarea value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} rows={3} data-testid="customer-notes" />
              </div>
              <div className="form-group-admin">
                <label>Κωδικός Σύνδεσης (προαιρετικός – αυτόματη δημιουργία αν κενό)</label>
                <input type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="Αφήστε κενό για αυτόματο" data-testid="customer-password" />
                <p style={{ fontSize: '0.75rem', color: '#999', marginTop: 4 }}>
                  Ο πελάτης θα μπορεί να συνδεθεί στην πύλη πελατών για να δει τις προσφορές του.
                </p>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Ακύρωση</button>
                <button type="submit" className="btn-primary" disabled={creating} data-testid="customer-submit-btn">{creating ? 'Δημιουργία...' : 'Δημιουργία'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credentials Display Modal */}
      {createdCredentials && (
        <div className="modal-overlay-admin" onClick={() => setCreatedCredentials(null)}>
          <div className="modal-admin" onClick={(e) => e.stopPropagation()} data-testid="credentials-modal">
            <div className="modal-header-admin">
              <h3>Στοιχεία Σύνδεσης Πελάτη</h3>
              <button onClick={() => setCreatedCredentials(null)}><X size={20} /></button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <p style={{ marginBottom: '1rem', color: '#666' }}>
                Δώστε αυτά τα στοιχεία στον πελάτη για να συνδεθεί στην πύλη:
              </p>
              <div style={{ background: '#f5f5ff', padding: '1rem', border: '1px solid #d0d0ff', display: 'grid', gap: '0.75rem' }}>
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#999' }}>Κωδικός Πελάτη</span>
                  <p style={{ margin: '0.2rem 0 0', fontWeight: 600, fontFamily: 'monospace', fontSize: '1.1rem' }}>
                    {createdCredentials.customer_number}
                  </p>
                </div>
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#999' }}>Username</span>
                  <p style={{ margin: '0.2rem 0 0', fontWeight: 600, fontFamily: 'monospace' }}>
                    {createdCredentials.username}
                  </p>
                </div>
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#999' }}>Password</span>
                  <p style={{ margin: '0.2rem 0 0', fontWeight: 600, fontFamily: 'monospace' }}>
                    {createdCredentials.password}
                  </p>
                </div>
              </div>
              <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#999' }}>
                Μπορείτε να δείτε ξανά αυτά τα στοιχεία στην καρτέλα «Στοιχεία» του πελάτη.
              </p>
              <div className="modal-actions">
                <button className="btn-primary" onClick={() => setCreatedCredentials(null)}>Εντάξει</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
